<template>
  <div class="language-selector" ref="root">
    <button type="button" class="lang-btn" @click="open = !open" :class="{ open }">
      <!-- The code is the icon: an outlined box drawn at the same weight as
           the glyphs beside it, so it belongs to the row without the code
           shrinking to a corner mark. -->
      <span class="lang-code">{{ currentLocale.toUpperCase() }}</span>
    </button>
    <div v-if="open" class="lang-dropdown">
      <button
        v-for="l in languages"
        :key="l.code"
        type="button"
        :class="{ active: currentLocale === l.code }"
        @click="select(l.code)"
      >
        {{ l.label }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();
const currentLocale = ref(locale.value);
const open = ref(false);
const root = ref(null);

const languages = [
  { code: 'en', label: 'English' },
  { code: 'bg', label: 'Български' },
  { code: 'ar', label: 'العربية' },
  { code: 'he', label: 'עברית' },
  { code: 'el', label: 'Ελληνικά' },
];

const select = (code) => {
  currentLocale.value = code;
  locale.value = code;
  localStorage.setItem('locale', code);
  open.value = false;
};

watch(locale, (newLocale) => {
  currentLocale.value = newLocale;
});

const onDocClick = (e) => {
  if (root.value && !root.value.contains(e.target)) open.value = false;
};
onMounted(() => document.addEventListener('click', onDocClick));
onUnmounted(() => document.removeEventListener('click', onDocClick));
</script>

<style scoped>
/* flex, not inline-block: an inline wrapper carries a line box whose
   descender space sits under the button, making this taller than the 40px it
   contains and lifting the label above the icons it stands next to. */
.language-selector { position: relative; display: flex; align-items: center; }

.lang-btn {
  position: relative;
  display: flex; align-items: center; justify-content: center;
  /* The same 40x40 square as the nav icons, with the outlined box centred
     inside it. */
  box-sizing: border-box;
  width: 40px; height: 40px; padding: 0;
  border: none; border-radius: 11px;
  /* The same ink as the nav icons. */
  background: transparent; color: var(--gray, #322e38);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}
/* Matches the nav icons beside it: blue, lifted two pixels, no box. Kept in
   sync with .btn-icon:hover in Navigation.vue. */
.lang-btn:hover, .lang-btn.open {
  color: #2b82f0;
  transform: translateY(-2px);
}

/* 1.5px to sit between the icons' 1.6 stroke and a border's whole pixels;
   currentColor so the outline turns blue with the text on hover rather than
   being a second thing to keep in step. */
.lang-code {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  border: 1.5px solid currentColor;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.03em;
  /* An optical correction: the code is all caps, so its mass sits above the
     baseline while the line box still reserves room for descenders. */
  padding-top: 1px;
}

.lang-dropdown {
  position: absolute; top: calc(100% + 8px); right: 0; z-index: 200;
  min-width: 160px; padding: 6px;
  background: #ffffff; border: 1px solid rgba(31, 41, 51, 0.08);
  border-radius: 14px; box-shadow: 0 24px 60px -34px rgba(31, 41, 51, 0.4);
  display: flex; flex-direction: column;
}
.lang-dropdown button {
  text-align: left; padding: 10px 12px; border: none; background: none;
  border-radius: 9px; font-size: 14px; font-weight: 600; color: var(--ink, #1f2933);
  cursor: pointer; transition: background 0.15s ease, color 0.15s ease;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}
.lang-dropdown button:hover { background: rgba(31, 41, 51, 0.05); }
/* The chosen language is said by its colour alone; a filled row behind it
   read as a second kind of hover. */
.lang-dropdown button.active { background: none; color: #2b82f0; }

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}
</style>
